import { SUPPLIER_RATING_DIMENSIONS, SUPPLIER_RATING_MAX } from '../constants';
import type { SupplierScorecard } from '../types';
import { SupplierScoreBadge } from './SupplierScoreBadge';

/**
 * One supplier's score broken down across the four rating dimensions.
 *
 * Presentational only — the caller supplies the already-aggregated scorecard.
 * That is what lets the RFQ comparison show a card per bidder from the single
 * `listScorecards(supplierIds)` call it already makes, instead of firing one
 * request per supplier from inside each row.
 *
 * The averages come from aggregateSupplierScores() on the server; nothing here
 * recomputes a score.
 */
export function SupplierScorecardPanel({
  scorecard,
  title,
}: {
  scorecard: SupplierScorecard;
  title?: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
            {title || scorecard.supplierName || 'Supplier'}
          </div>
          <div className="text-xs text-slate-400">
            {scorecard.ratingCount === 0
              ? 'No ratings recorded yet'
              : `${scorecard.ratingCount} rating${scorecard.ratingCount === 1 ? '' : 's'}`}
          </div>
        </div>
        <SupplierScoreBadge
          score={scorecard.overallScore}
          band={scorecard.band}
          ratingCount={scorecard.ratingCount}
          showCount={false}
        />
      </div>

      <div className="space-y-2">
        {SUPPLIER_RATING_DIMENSIONS.map(dimension => {
          const value = scorecard.averages?.[dimension.key];
          const pct = value === null || value === undefined ? 0 : (value / SUPPLIER_RATING_MAX) * 100;
          return (
            <div key={dimension.key}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-slate-500 dark:text-slate-400">{dimension.label}</span>
                <span className="font-semibold text-slate-700 dark:text-slate-300">
                  {value === null || value === undefined ? '—' : value.toFixed(1)}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                <div className="h-full rounded-full bg-indigo-500" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default SupplierScorecardPanel;
